import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import inOutOneWebm from "../../assets/inOutOne.webm";
import inOutTwoWebm from "../../assets/inOutTwo.webm";
import inOutThreeWebm from "../../assets/inOutThree.webm";
import inOutFourWebm from "../../assets/inOutFour.webm";
import inOutOnePoster from "../../assets/inOutOne.png";
import inOutTwoPoster from "../../assets/inOutTwo.png";
import inOutThreePoster from "../../assets/inOutThree.png";
import inOutFourPoster from "../../assets/inOutFour.png";
import IntroCard from "../Cards/IntroCard";
import useTypewriter from "../../hooks/useTypewriter";
import useOnScreen from "../../hooks/useOnScreen";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const IntroOutro = () => {
  const ref = useRef(null);
  const [activeTab, setActiveTab] = useState("intro");
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.3 });
  const { ref: tabsRef, isVisible: tabsVisible } = useScrollAnimation({ threshold: 0.3 });

  const isVisible = useOnScreen(ref);

  const fullSubtitle =
    "Use our ready-made animated intros and \n outros or design one that's 100% you.";
  const typedSubtitle = useTypewriter(fullSubtitle, 50, isVisible);

  const lines = typedSubtitle.split("\n");

  // Define tabs
  const tabs = [
    { id: "intro", label: "Intro & Outro" },
    { id: "transitions", label: "Transitions" },
    { id: "animated", label: "Animated Assets" },
    { id: "vfx", label: "VFX Elements" }
  ];

  // Define content for each tab (4 cards per tab)
  const tabContent = {
    intro: [
      { srcWebm: inOutOneWebm, poster: inOutOnePoster, isVideo: true },
      { srcWebm: inOutTwoWebm, poster: inOutTwoPoster, isVideo: true },
      { srcWebm: inOutThreeWebm, poster: inOutThreePoster, isVideo: true },
      { srcWebm: inOutFourWebm, poster: inOutFourPoster, isVideo: true }
    ],
    transitions: [
      { srcWebm: inOutOneWebm, poster: inOutOnePoster, isVideo: true },
      { srcWebm: inOutTwoWebm, poster: inOutTwoPoster, isVideo: true },
      { srcWebm: inOutThreeWebm, poster: inOutThreePoster, isVideo: true },
      { srcWebm: inOutFourWebm, poster: inOutFourPoster, isVideo: true }
    ],
    animated: [
      { srcWebm: inOutOneWebm, poster: inOutOnePoster, isVideo: true },
      { srcWebm: inOutTwoWebm, poster: inOutTwoPoster, isVideo: true },
      { srcWebm: inOutThreeWebm, poster: inOutThreePoster, isVideo: true },
      { srcWebm: inOutFourWebm, poster: inOutFourPoster, isVideo: true }
    ],
    vfx: [
      { srcWebm: inOutOneWebm, poster: inOutOnePoster, isVideo: true },
      { srcWebm: inOutTwoWebm, poster: inOutTwoPoster, isVideo: true },
      { srcWebm: inOutThreeWebm, poster: inOutThreePoster, isVideo: true },
      { srcWebm: inOutFourWebm, poster: inOutFourPoster, isVideo: true }
    ]
  };

  return (
    <div className="bg-ag-black-800 w-full">
      <div className="flex flex-col pt-2 sm:pt-3 md:pt-4">
        {/* Header section with slide animations */}
        <div 
          ref={headerRef}
          className="flex flex-col md:flex-row ml-2 sm:ml-4 md:ml-8 lg:ml-10 xl:ml-12 py-4 sm:py-5 md:py-6 lg:py-7 xl:py-8 px-3 sm:px-4 md:px-5 lg:px-6 xl:px-8 mb-4 sm:mb-6 md:mb-8 lg:mb-10 gap-4 sm:gap-6 md:gap-10 lg:gap-12 xl:gap-16 md:justify-between items-center"
        >
          <p className={`text-white font-sourceSans text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-8xl text-center md:text-left transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
          }`}>
            <span className="font-light italic">Intros & Outros?</span> <span className="block font-sourceSans font-black tracking-tighter">Covered</span>
          </p>
          <div className={`flex flex-col w-full md:w-1/2 md:max-w-2xl items-center md:items-start transition-all duration-700 ${
            headerVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
          }`} style={{ transitionDelay: '200ms' }}>
            <p ref={ref} className="text-ag-black-300 font-medium text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl mb-3 sm:mb-4 min-h-[3rem] sm:min-h-[3.5rem] md:min-h-[4rem] lg:min-h-[5rem] xl:min-h-[6rem] text-center md:text-left">
              <span>{lines[0]}</span>
              {lines[1] && <span className="block">{lines[1]}</span>}
              {isVisible && typedSubtitle.length < fullSubtitle.length && (
                <span className="animate-pulse text-ag-green">|</span>
              )}
            </p>
            <Link to="/templates">
              <button className="max-w-[150px] sm:max-w-[170px] md:max-w-[200px] bg-ag-green hover:bg-ag-green/90 px-3 sm:px-4 md:px-5 lg:px-6 py-1.5 sm:py-2 md:py-2.5 lg:py-3 text-black rounded-lg transition-all duration-300 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-sourceSans tracking-tighter font-bold hover:scale-105 hover:shadow-lg hover:shadow-ag-green/30 btn-press ripple-effect">
                Try for free
              </button>
            </Link>
          </div>
        </div>
        
        {/* Tabs with scale animation */}
        <div 
          ref={tabsRef}
          className={`flex justify-center mb-4 sm:mb-5 md:mb-6 lg:mb-7 xl:mb-8 px-2 sm:px-3 md:px-4 transition-all duration-700 ${
            tabsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="max-w-5xl flex flex-wrap sm:flex-nowrap justify-center sm:justify-between gap-2 sm:gap-3 md:gap-4 px-2 sm:px-3 md:px-4 py-2 sm:py-3 md:py-4 overflow-x-auto border border-ag-black-600 rounded-xl sm:rounded-2xl md:rounded-3xl w-full">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap text-xs sm:text-sm md:text-base lg:text-lg font-semibold px-3 sm:px-4 md:px-6 lg:px-8 py-1.5 sm:py-2 rounded-lg sm:rounded-xl transition-all duration-300 focus:outline-none focus:ring-0 focus:ring-offset-0 hover:scale-105 btn-press ${
                  activeTab === tab.id
                    ? "bg-primary-50 text-black hover:bg-ag-green border-2 sm:border-3 md:border-4 border-ag-green shadow-lg shadow-ag-green/20"
                    : "bg-ag-black-700 hover:bg-ag-black-600 font-sourceSans font-bold text-ag-black-300 border sm:border-2 border-black hover:border-ag-green/50"
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cards Grid with staggered animations */}
        <div
          key={activeTab}
          className="grid grid-cols-2 md:grid-cols-4 gap-1 sm:gap-1.5 md:gap-2 w-full"
        >
          {tabContent[activeTab].map((card, index) => (
            <div
              key={`${activeTab}-${index}`}
              className="animate-slideUp hover:scale-[1.02] transition-transform duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <IntroCard
                src={card.src}
                srcWebm={card.srcWebm}
                srcMp4={card.srcMp4}
                poster={card.poster}
                isVideo={card.isVideo}
                className="hover:shadow-xl hover:shadow-ag-green/10 transition-shadow duration-300"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntroOutro;
